import { useCallback, useRef } from 'react';
import { detectDateInText } from '../utils/taskUtils';
import { isSequentialTask, getSequenceNumber } from '../utils/sequentialTasks';

/**
 * Hook for handling task text input, focus, and blur
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Object} params.editingElementRef - Ref to the editing element
 * @param {Function} params.updateTaskText - Function to update task text
 * @param {Function} params.updateTaskTextLocal - Function to update task text locally
 * @param {Function} params.updateTaskDueDate - Function to update task due date
 * @param {Function} params.createTask - Function to create a new task
 * @param {Object} params.placeholderStatusRef - Ref to placeholder checkbox status (when no tasks)
 * @param {Object} params.placeholderDueDateRef - Ref to placeholder due date (when no tasks)
 * @param {Object} params.placeholderIsLaterDateRef - Ref to whether placeholder due is "Later" (when no tasks)
 * @param {Object} params.skipPlaceholderCaretToStartRef - When true, do not move caret to start on placeholder focus (e.g. when restoring focus after due date set)
 */
export function useTaskTextInput({
  tasks,
  setEditingTaskId,
  editingElementRef,
  updateTaskText,
  updateTaskTextLocal,
  updateTaskDueDate,
  createTask,
  placeholderStatusRef,
  placeholderDueDateRef,
  placeholderIsLaterDateRef,
  skipPlaceholderCaretToStartRef
}) {
  // Store original text when editing starts to compare on blur
  const originalTextOnEditStartRef = useRef(new Map()); // Map<clientId, originalText>

  const handleTaskTextInput = useCallback((clientId, e) => {
    // Handle placeholder task
    if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
      let text = e.target.textContent || '';
      if (text.length === 1 && /^[a-z]/.test(text)) {
        const fixed = text.charAt(0).toUpperCase() + text.slice(1);
        e.target.textContent = fixed;
        text = fixed;
        const sel = window.getSelection();
        if (sel && e.target.firstChild) {
          const range = document.createRange();
          range.setStart(e.target.firstChild, Math.min(fixed.length, e.target.firstChild.length || 0));
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      // Empty = no characters (space alone should hide ::before)
      if (text.length > 0) {
        e.target.classList.remove('task-text-placeholder-empty');
      } else {
        e.target.classList.add('task-text-placeholder-empty');
      }
      return;
    }
    
    // Find task by clientId
    const task = tasks.find(t => t._reactKey === clientId);
    if (!task) return;
    
    // Get current text from the input element
    let currentText = e.target.textContent || e.target.innerText || '';

    // Capitalize when the first letter is added (beforeinput is unreliable with contentEditable on mobile).
    // Applies to: (1) first character of any task, (2) first character after "X. " in a sequential task.
    const isFirstCharOnly = currentText.length === 1 && /^[a-z]/.test(currentText);
    const isSequentialFirstChar = /^(\d+)\.\s?[a-z]$/.test(currentText);
    if (isFirstCharOnly || isSequentialFirstChar) {
      const fixed = isFirstCharOnly
        ? currentText.charAt(0).toUpperCase() + currentText.slice(1)
        : currentText.slice(0, -1) + currentText.slice(-1).toUpperCase();
      e.target.textContent = fixed;
      currentText = fixed;
      updateTaskTextLocal(task.id, fixed);
      const sel = window.getSelection();
      if (sel && e.target.firstChild) {
        const range = document.createRange();
        range.setStart(e.target.firstChild, Math.min(fixed.length, e.target.firstChild.length || 0));
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      return;
    }

    // Update local state only (not saved to server) so blocked status updates immediately
    // This allows the checkbox to update when numbering is added/removed
    // The actual save will happen on blur
    if (currentText !== task.text) {
      updateTaskTextLocal(task.id, currentText); // Update local state without saving
    }
  }, [tasks, updateTaskTextLocal]);

  const handleTaskTextFocus = useCallback((clientId) => {
    setEditingTaskId(clientId);
    
    // Store the original text when editing starts (before any local updates)
    const task = tasks.find(t => t._reactKey === clientId);
    if (task && clientId !== 'placeholder' && clientId !== 'placeholder_client_key') {
      originalTextOnEditStartRef.current.set(clientId, task.text || '');
    }
    
    setTimeout(() => {
      const element = document.querySelector(`.task-text[data-react-key="${clientId}"]`);
      if (element) {
        // Handle placeholder task - empty = no characters (space alone should hide ::before)
        if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
          const content = element.textContent || '';
          if (content.length === 0) {
            element.textContent = '';
            element.classList.add('task-text-placeholder-empty');
          } else {
            element.classList.remove('task-text-placeholder-empty');
          }
          // Position cursor at start unless we're restoring focus after due date set (avoid moving caret to 0)
          const skipCaretToStart = skipPlaceholderCaretToStartRef?.current;
          if (skipPlaceholderCaretToStartRef) skipPlaceholderCaretToStartRef.current = false;
          if (!skipCaretToStart) {
            const range = document.createRange();
            range.setStart(element, 0);
            range.collapse(true);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          }
          return;
        }
        
        const task = tasks.find(t => t._reactKey === clientId);
        if (task && (!element.textContent || element.textContent.trim() === '')) {
          element.textContent = task.text || '';
        }
        // Sequential prefix-only task (e.g. "5." or "5. "): ensure nbsp so space doesn't collapse, and move caret to end
        if (task && isSequentialTask(task)) {
          const trimmed = (task.text || '').trim();
          if (/^\d+\.\s*$/.test(trimmed)) {
            const num = getSequenceNumber(task);
            if (num !== null) {
              const normalizedText = `${num}.\u00A0`;
              element.textContent = normalizedText;
              updateTaskTextLocal(task.id, normalizedText);
              const range = document.createRange();
              range.selectNodeContents(element);
              range.collapse(false);
              const sel = window.getSelection();
              if (sel) {
                sel.removeAllRanges();
                sel.addRange(range);
              }
            }
          }
        }
      }
    }, 0);
  }, [tasks, setEditingTaskId, updateTaskTextLocal]);

  const handleTaskTextBlur = useCallback(async (clientId, e) => {
    // Don't exit edit mode when focus moves to the task edit toolbar's calendar (portal).
    // Otherwise the calendar unmounts before the day selection click is processed.
    if (e.relatedTarget?.closest?.('[data-task-edit-toolbar-calendar]')) {
      return;
    }
    // Fallback: some browsers don't set relatedTarget when clicking the calendar; if the
    // calendar portal is open, don't exit so the day selection can complete.
    if (!e.relatedTarget && typeof document !== 'undefined' && document.querySelector('[data-task-edit-toolbar-calendar]')) {
      return;
    }

    // CRITICAL: Read text from DOM FIRST before any state updates that might modify the DOM
    // This ensures we capture the complete text the user typed
    // Use both textContent and innerText to ensure we get the full text
    let currentText = '';
    if (e.target.textContent !== null && e.target.textContent !== undefined) {
      currentText = e.target.textContent;
    } else if (e.target.innerText !== null && e.target.innerText !== undefined) {
      currentText = e.target.innerText;
    }
    // Fallback: try to get text from the element's text nodes directly
    if (!currentText && e.target.childNodes) {
      currentText = Array.from(e.target.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE)
        .map(node => node.textContent)
        .join('');
    }
    const trimmedText = currentText.trim();
    
    // Now update state (this might trigger DOM updates in TaskText component)
    setEditingTaskId(null);
    editingElementRef.current = null;
    
    // Handle placeholder task - create real task when user types
    if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
      if (trimmedText) {
        // Calculate order for the first task - should be 1 if no tasks exist
        const rootTasks = tasks.filter(t => t.parent_task_id === null);
        const maxOrder = rootTasks.length > 0
          ? Math.max(...rootTasks.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
          : 0;
        const newOrder = maxOrder + 1;
        const status = placeholderStatusRef?.current ?? 'incomplete';
        const placeholderDueDate = placeholderDueDateRef?.current ?? null;
        const placeholderIsLater =
          placeholderIsLaterDateRef?.current === true;
        createTask(
          trimmedText,
          null,
          newOrder,
          status,
          placeholderDueDate,
          null,
          placeholderIsLater
        );
      } else {
        // Keep empty so placeholder text shows
        e.target.textContent = '';
        e.target.classList.add('task-text-placeholder-empty');
      }
      return;
    }
    
    const originalTask = tasks.find(t => t._reactKey === clientId);
    if (!originalTask) return;
    
    // Get the original text from when editing started (before any local updates)
    const originalTextOnEditStart = originalTextOnEditStartRef.current.get(clientId);
    const originalTextTrimmed = originalTextOnEditStart !== undefined 
      ? (originalTextOnEditStart || '').trim()
      : (originalTask.text || '').trim(); // Fallback to current task text if not stored
    
    // Compare trimmed versions to check if text actually changed
    const textChanged = trimmedText !== originalTextTrimmed;
    
    // Clean up the stored original text
    originalTextOnEditStartRef.current.delete(clientId);
    
    // Only save if the text actually changed
    if (textChanged) {
      if (trimmedText) {
        // Save the full trimmed text from DOM
        updateTaskText(originalTask.id, trimmedText); // Use database ID
        
        // Detect and set due date if a human-readable date is found in the text
        // Only do this on blur to avoid saving incomplete dates while typing
        const detectedDate = detectDateInText(trimmedText);
        if (detectedDate) {
          updateTaskDueDate(originalTask.id, detectedDate); // Use database ID
        }
      } else {
        // Text was changed to empty - save empty string
        updateTaskText(originalTask.id, ''); // Use database ID
      }
    } else if (!trimmedText && originalTextTrimmed) {
      // Text is empty but original had text - restore it (user might have accidentally deleted)
      e.target.textContent = originalTextTrimmed;
    }
    // If text didn't change, don't make any server requests
  }, [tasks, setEditingTaskId, editingElementRef, updateTaskText, updateTaskDueDate, createTask]);

  return {
    handleTaskTextInput,
    handleTaskTextFocus,
    handleTaskTextBlur
  };
}
